import { GridPinnedColumns, GridSortItem } from '@mui/x-data-grid-pro'

import { PageSizeOption, UIFilters } from 'routes/highschools/types'

import { standardViews } from './defaultViews'
import { HSExploreView, HSExploreViews } from './types'

type State = {
  viewData: HSExploreViews
  activeView: HSExploreView
  hasError: boolean
  errorMsg?: string
  currentPage: number
  pageSize: PageSizeOption
  isLoading: boolean
}

type Action =
  | { type: 'set-active-view'; view: HSExploreView }
  | { type: 'load-views'; data: LoadViews }
  | { type: 'set-error'; hasError: boolean }
  | { type: 'create-view'; view: HSExploreView; isDefault: boolean }
  | { type: 'update-view'; view: HSExploreView }
  | { type: 'update-column-visibility'; visibilityModel: { [key: string]: boolean } }
  | { type: 'update-column-order'; orderModel: { [key: string]: number } }
  | { type: 'delete-view'; viewId: string }
  | { type: 'update-pinned-columns'; pinnedColumns: GridPinnedColumns }
  | { type: 'update-column-width'; widthModel: { [key: string]: number } }
  | { type: 'update-default-view'; id: string }
  | { type: 'update-column-sort'; sortModel: GridSortItem }
  | { type: 'pageSize-change'; pageSize: PageSizeOption }
  | {
      type: 'page-change'
      page: number
    }
  | { type: 'update-column-filters'; filters: UIFilters }
  | { type: 'update-column-config'; field: string; config: Record<string, unknown> }

type LoadViews = {
  views: HSExploreView[]
  activeView?: HSExploreView
  defaultViewId?: string
  currentPage: number
  pageSize: PageSizeOption
}

export const initialState: State = {
  activeView: standardViews.views[0],
  currentPage: 0,
  pageSize: 25,
  viewData: standardViews,
  hasError: false,
  isLoading: true,
}

export default function viewsReducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'set-active-view':
      return {
        ...state,
        activeView: action.view,
        currentPage: 0,
      }
    case 'load-views': {
      const defaultView = action.data.views.find((v) => v.id === action.data.defaultViewId) || standardViews.views[0]
      return {
        ...state,
        viewData: {
          ...state.viewData,
          views: action.data.views,
          defaultViewId: defaultView.id,
        },
        isLoading: false,
        currentPage: action.data.currentPage,
        activeView: action.data.activeView ? action.data.activeView : defaultView,
        pageSize: action.data.pageSize,
        hasError: false,
      }
    }
    case 'create-view': {
      return {
        ...state,
        activeView: action.view,
        viewData: {
          ...state.viewData,
          views: [...state.viewData.views, action.view],
          defaultViewId: action.isDefault ? action.view.id : state.viewData.defaultViewId,
        },
      }
    }
    case 'update-view': {
      const indexToReplace = state.viewData.views.findIndex((view) => view.id === action.view.id)
      if (indexToReplace === -1) return state
      let defaultViewId = state.viewData.defaultViewId
      if (action.view.isDefault && action.view.id) {
        defaultViewId = action.view.id
      }

      const updatedViews = [...state.viewData.views]
      updatedViews[indexToReplace] = action.view

      return {
        ...state,
        viewData: {
          ...state.viewData,
          views: updatedViews,
          defaultViewId,
        },
        activeView: state.activeView?.id === action.view.id ? action.view : state.activeView,
      }
    }
    case 'update-column-visibility':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          visibilityModel: action.visibilityModel,
        },
      }
    case 'update-pinned-columns':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          pinnedModel: action.pinnedColumns,
        },
      }
    case 'update-column-order':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          orderModel: action.orderModel,
        },
      }
    case 'update-column-width':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          widthModel: action.widthModel,
        },
      }
    case 'update-column-filters':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          filterModel: action.filters,
        },
      }
    case 'update-column-sort':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          sortModel: action.sortModel,
        },
      }
    case 'update-column-config':
      return {
        ...state,
        activeView: {
          ...state.activeView,
          columnConfigModel: {
            ...(state.activeView.columnConfigModel ?? {}),
            [action.field]: action.config,
          },
        },
      }
    case 'delete-view': {
      const updatedViews = state.viewData.views.filter((view) => view.id !== action.viewId)
      const defaultView = updatedViews.find((v) => v.id === state.viewData.defaultViewId) || standardViews.views[0]
      return {
        ...state,
        viewData: {
          ...state.viewData,
          views: updatedViews,
          defaultViewId: defaultView.id,
        },
        activeView: state.activeView?.id === action.viewId ? defaultView : state.activeView,
      }
    }
    case 'update-default-view':
      return {
        ...state,
        viewData: {
          ...state.viewData,
          defaultViewId: action.id,
        },
      }
    case 'pageSize-change':
      return {
        ...state,
        pageSize: action.pageSize,
      }
    case 'page-change':
      return {
        ...state,
        currentPage: action.page,
      }
    case 'set-error':
      return {
        ...state,
        hasError: action.hasError,
        isLoading: false,
      }

    default:
      return state
  }
}
